import type { Product } from "@/types";
import { categoryBadge } from "@/utils/categories";
import { formatCOP, formatNumber } from "@/utils/format";

interface ProductTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
}

const ProductTable = ({ products, onEdit, onDelete }: ProductTableProps) => {
  if (products.length === 0) {
    return (
      <div className="glass-panel rounded-lg p-10 text-center">
        <span className="mx-auto w-14 h-14 rounded-lg bg-accent-500/15 text-accent-200 flex items-center justify-center mb-4">
          <i className="ri-search-eye-line text-2xl" />
        </span>
        <h3 className="editable-title text-base font-bold">Sin productos para mostrar</h3>
        <p className="text-xs text-foreground-600 mt-1">
          Ajusta la búsqueda o agrega un nuevo producto al catálogo maestro.
        </p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-lg overflow-hidden">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[960px] text-left">
          <thead>
            <tr className="border-b border-accent-500/20 text-[11px] uppercase tracking-wide text-foreground-500">
              <th className="px-4 py-3 font-semibold">Foto</th>
              <th className="px-4 py-3 font-semibold">SKU</th>
              <th className="px-4 py-3 font-semibold">Producto</th>
              <th className="px-4 py-3 font-semibold">Categoría</th>
              <th className="px-4 py-3 font-semibold text-right">PV</th>
              <th className="px-4 py-3 font-semibold text-right">Costo base</th>
              <th className="px-4 py-3 font-semibold text-right">Precio público</th>
              <th className="px-4 py-3 font-semibold text-right">Margen</th>
              <th className="px-4 py-3 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {products.map((p) => {
              const margin =
                p.pricePublic > 0
                  ? ((p.pricePublic - p.costBase) / p.pricePublic) * 100
                  : 0;
              return (
                <tr
                  key={p.id || p.sku}
                  className="border-b border-background-300/25 hover:bg-background-200/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="w-11 h-11 rounded-md border border-background-300/40 bg-background-200/40 flex items-center justify-center overflow-hidden">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.name} className="w-full h-full object-contain" />
                      ) : (
                        <i className="ri-image-line text-foreground-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-accent-200 whitespace-nowrap">{p.sku}</td>
                  <td className="px-4 py-3 font-medium text-foreground-900 max-w-[280px]">{p.name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${categoryBadge(p.category)}`}>
                      {p.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-foreground-800 whitespace-nowrap">
                    {formatNumber(p.pv)}
                  </td>
                  <td className="px-4 py-3 text-right text-foreground-700 whitespace-nowrap">
                    {formatCOP(p.costBase)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-foreground-950 whitespace-nowrap">
                    {formatCOP(p.pricePublic)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="text-accent-200 font-semibold">{margin.toFixed(0)}%</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => onEdit(p)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:text-accent-200 hover:bg-background-300/40 cursor-pointer"
                        aria-label="Editar producto"
                        title="Editar"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        onClick={() => onDelete(p)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:text-primary-200 hover:bg-background-300/40 cursor-pointer"
                        aria-label="Eliminar producto"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Vista móvil en tarjetas */}
      <ul className="md:hidden divide-y divide-background-300/25">
        {products.map((p) => {
          const margin =
            p.pricePublic > 0 ? ((p.pricePublic - p.costBase) / p.pricePublic) * 100 : 0;
          return (
            <li key={p.id || p.sku} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-12 h-12 shrink-0 rounded-md border border-background-300/40 bg-background-200/40 flex items-center justify-center overflow-hidden">
                    {p.imageUrl ? (
                      <img src={p.imageUrl} alt={p.name} className="w-full h-full object-contain" />
                    ) : (
                      <i className="ri-image-line text-foreground-500" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-900 break-words">{p.name}</p>
                    <p className="font-mono text-[10px] text-accent-200 mt-0.5">{p.sku}</p>
                  </div>
                </div>
                <span
                  className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${categoryBadge(p.category)}`}
                >
                  {p.category}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2.5 py-1.5">
                  <p className="text-foreground-500">Costo base</p>
                  <p className="font-semibold text-foreground-800">{formatCOP(p.costBase)}</p>
                </div>
                <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2.5 py-1.5">
                  <p className="text-foreground-500">Precio público</p>
                  <p className="font-semibold text-foreground-950">{formatCOP(p.pricePublic)}</p>
                </div>
                <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2.5 py-1.5">
                  <p className="text-foreground-500">Puntos PV</p>
                  <p className="font-semibold text-foreground-800">{formatNumber(p.pv)}</p>
                </div>
                <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2.5 py-1.5">
                  <p className="text-foreground-500">Margen</p>
                  <p className="font-semibold text-accent-200">{margin.toFixed(0)}%</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-2">
                <button
                  onClick={() => onEdit(p)}
                  className="px-3 py-1.5 rounded-md border border-accent-500/35 text-accent-200 hover:bg-accent-500/10 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                >
                  <i className="ri-pencil-line" /> Editar
                </button>
                <button
                  onClick={() => onDelete(p)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                  aria-label="Eliminar producto"
                  title="Eliminar"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-accent-500/15 text-[11px] text-foreground-500">
        <span>{formatNumber(products.length)} referencias visibles</span>
        <span className="flex items-center gap-1.5">
          <i className="ri-information-line" /> PV, costo y precio se recalculan en tiempo real
        </span>
      </div>
    </div>
  );
};

export default ProductTable;